/**
 * @file
 * @fileOverview This file contains utility components for the Aframe scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

/**
 * A-Frame component to collect scene data and send it to an endpoint via AJAX.
 */
AFRAME.registerComponent('scene-collector', {
    schema: {
        excludeElements: { type: 'array', default: ['a-assets'] },
        autoUpdate: { type: 'boolean', default: false },
        updateInterval: { type: 'number', default: 1000 }, // in milliseconds
        includeInvisible: { type: 'boolean', default: true },
        includeChildCount: { type: 'boolean', default: true },
        // AJAX settings
        enableAjax: { type: 'boolean', default: false },
        endpoint: { type: 'string', default: '' },
        ajaxMethod: { type: 'string', default: 'POST' },
        ajaxHeaders: { type: 'string', default: '{}' }, // JSON string of headers
        ajaxCredentials: { type: 'string', default: 'same-origin' }, // 'include', 'same-origin', or 'omit'
        retryOnError: { type: 'boolean', default: true },
        maxRetries: { type: 'number', default: 3 },
        // Socket.IO settings
        enableSocketIO: { type: 'boolean', default: false },
        socketUrl: { type: 'string', default: '' },
        socketPath: { type: 'string', default: '/socket.io' },
        socketRoom: { type: 'string', default: '' },
        socketEventName: { type: 'string', default: 'scene-data' },
        socketReconnectAttempts: { type: 'number', default: 5 },
        socketReconnectDelay: { type: 'number', default: 1000 },
        debug: { type: 'boolean', default: false }
    },

    init: function() {
        this.retryCount = 0;
        this.socket = null;
        this.socketReconnectAttempts = 0;
        this.sceneInfo = {
            objects: [],
            sceneMetadata: {},
            stats: {
                totalObjects: 0,
                visibleObjects: 0,
                totalLights: 0,
                totalCameras: 0,
                totalMeshes: 0,
                totalAnimations: 0
            },
            timestamp: null
        };

        this.collectSceneData = this.collectSceneData.bind(this);
        
        // Initialize connections based on configuration
        if (this.data.enableSocketIO) {
            this.initSocketIO();
        }

        // Start auto-updates if enabled
        if (this.data.autoUpdate) {
            this.startAutoUpdate();
        }

        // Expose public methods
        this.el.sceneCollector = {
            collect: this.collectSceneData,
            getData: () => this.sceneInfo,
            getObjectById: (id) => this.findObjectById(id),
            socket: () => this.socket
        };
    },

    loadSocketIOScript: function() {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/socket.io/4.7.2/socket.io.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    },

    initSocketIO: function() {
        if (!this.data.socketUrl) {
            console.error('Socket.IO URL not provided');
            return;
        }

        if (!window.io) {
            this.loadSocketIOScript()
                .then(() => this.setupSocketConnection())
                .catch(error => {
                    console.error('Failed to load Socket.IO client:', error);
                });
            return;
        }

        this.setupSocketConnection();
    },

    setupSocketConnection: function() {
        this.socket = io(this.data.socketUrl, {
            path: this.data.socketPath,
            reconnection: true,
            reconnectionAttempts: this.data.socketReconnectAttempts,
            reconnectionDelay: this.data.socketReconnectDelay,
            timeout: 10000
        });

        this.socket.on('connect', () => {
            this.log('Socket.IO connected');
            this.socketReconnectAttempts = 0;
            
            // Join room if specified
            if (this.data.socketRoom) {
                this.socket.emit('join-room', this.data.socketRoom);
            }

            this.el.emit('scene-collector-socket-connected');
        });

        this.socket.on('disconnect', () => {
            this.log('Socket.IO disconnected');
            this.el.emit('scene-collector-socket-disconnected');
        });

        this.socket.on('connect_error', (error) => {
            this.log('Socket.IO connection error:', error);
            this.socketReconnectAttempts++;
            this.el.emit('scene-collector-socket-error', { error });
        });
    },

    sendDataToSocket: function() {
        if (!this.socket || !this.socket.connected) {
            this.log('Socket.IO not connected');
            return;
        }

        try {
            this.socket.emit(this.data.socketEventName, {
                roomId: this.data.socketRoom,
                data: this.sceneInfo
            });
            
            this.el.emit('scene-data-sent', {
                success: true,
                method: 'socket.io',
                eventName: this.data.socketEventName
            });
        } catch (error) {
            this.log('Error sending scene data via Socket.IO:', error);
            this.el.emit('scene-data-sent', {
                success: false,
                method: 'socket.io',
                error: error.message
            });
        }
    },

    sendDataToEndpoint: async function() {
        if (!this.data.enableAjax || !this.data.endpoint) {
            this.log('AJAX is not enabled or endpoint is not configured');
            return;
        }

        try {
            // Parse headers from JSON string
            const headers = JSON.parse(this.data.ajaxHeaders);
            
            // Add default content-type if not specified
            if (!headers['Content-Type']) {
                headers['Content-Type'] = 'application/json';
            }

            const response = await fetch(this.data.endpoint, {
                method: this.data.ajaxMethod,
                headers: headers,
                credentials: this.data.ajaxCredentials,
                body: JSON.stringify(this.sceneInfo)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Reset retry count on successful send
            this.retryCount = 0;
            
            // Emit success event
            this.el.emit('scene-data-sent', {
                success: true,
                method: 'ajax',
                status: response.status,
                endpoint: this.data.endpoint
            });

            return response;

        } catch (error) {
            this.log('Error sending scene data:', error);

            // Handle retries if enabled
            if (this.data.retryOnError && this.retryCount < this.data.maxRetries) {
                this.retryCount++;
                this.log(`Retrying... Attempt ${this.retryCount} of ${this.data.maxRetries}`);
                
                // Retry after a delay (exponential backoff)
                const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 10000);
                setTimeout(() => this.sendDataToEndpoint(), retryDelay);
            }

            // Emit error event
            this.el.emit('scene-data-sent', {
                success: false,
                method: 'ajax',
                error: error.message,
                retryCount: this.retryCount,
                endpoint: this.data.endpoint
            });

            throw error;
        }
    },

    collectSceneData: function() {
        const elements = this.el.querySelectorAll('*');
        this.sceneInfo.objects = [];
        this.sceneInfo.stats = {
            totalObjects: 0,
            visibleObjects: 0,
            totalLights: 0,
            totalCameras: 0,
            totalMeshes: 0,
            totalAnimations: 0
        };

        elements.forEach(element => {
            // Skip excluded elements
            if (this.data.excludeElements.includes(element.tagName.toLowerCase())) {
                return;
            }

            // Skip invisible elements if configured
            if (!this.data.includeInvisible && element.object3D && !element.object3D.visible) {
                return;
            }

            const elementData = this.collectElementData(element);
            this.sceneInfo.objects.push(elementData);

            // Update stats
            this.sceneInfo.stats.totalObjects++;
            if (element.object3D?.visible) this.sceneInfo.stats.visibleObjects++;
            if (element.hasAttribute('light')) this.sceneInfo.stats.totalLights++;
            if (element.hasAttribute('camera')) this.sceneInfo.stats.totalCameras++;
            if (element.getObject3D('mesh')) this.sceneInfo.stats.totalMeshes++;
            if (element.hasAttribute('animation')) this.sceneInfo.stats.totalAnimations++;
        });

        // Collect scene metadata
        this.sceneInfo.sceneMetadata = this.collectSceneMetadata();
        this.sceneInfo.timestamp = new Date().toISOString();

        // Emit an event with the collected data
        this.el.emit('scene-data-updated', {
            data: this.sceneInfo
        });

        // Send data through configured channels
        if (this.data.enableAjax) {
            this.sendDataToEndpoint().catch(error => {
                this.log('Failed to send scene data via AJAX:', error);
            });
        }

        if (this.data.enableSocketIO && this.socket) {
            this.sendDataToSocket();
        }

        return this.sceneInfo;
    },

    collectElementData: function(element) {
        return {
            id: element.id || null,
            tagName: element.tagName.toLowerCase(),
            class: element.className || null,
            visible: element.object3D ? element.object3D.visible : null,
            components: this.collectComponentData(element),
            attributes: this.collectAttributes(element),
            transform: this.collectTransformData(element),
            boundingBox: this.getBoundingBox(element),
            object3D: this.collectObject3DData(element)
        };
    },

    collectComponentData: function(element) {
        const components = {};
        const componentNames = Object.keys(element.components || {});
        
        componentNames.forEach(name => {
            if (element.components[name]?.data) {
                components[name] = {
                    data: element.components[name].data,
                    multiple: element.components[name].multiple || false,
                    attrValue: element.getAttribute(name)
                };
            }
        });

        return components;
    },

    collectAttributes: function(element) {
        const attributes = {};
        Array.from(element.attributes).forEach(attr => {
            if (!['id', 'class'].includes(attr.name)) {
                attributes[attr.name] = attr.value;
            }
        });
        return attributes;
    },

    collectTransformData: function(element) {
        return {
            local: {
                position: element.getAttribute('position'),
                rotation: element.getAttribute('rotation'),
                scale: element.getAttribute('scale')
            },
            world: {
                position: this.getWorldPosition(element),
                rotation: this.getWorldRotation(element),
                scale: this.getWorldScale(element)
            }
        };
    },

    collectObject3DData: function(element) {
        return element.object3D ? {
            type: element.object3D.type,
            uuid: element.object3D.uuid,
            matrixAutoUpdate: element.object3D.matrixAutoUpdate
        } : null;
    },

    getWorldPosition: function(element) {
        if (!element.object3D) return null;
        const pos = new THREE.Vector3();
        element.object3D.getWorldPosition(pos);
        return {
            x: Number(pos.x.toFixed(4)),
            y: Number(pos.y.toFixed(4)),
            z: Number(pos.z.toFixed(4))
        };
    },

    getWorldRotation: function(element) {
        if (!element.object3D) return null;
        const rot = new THREE.Euler();
        element.object3D.getWorldQuaternion(new THREE.Quaternion()).extractEuler(rot);
        return {
            x: Number((rot.x * THREE.MathUtils.RAD2DEG).toFixed(4)),
            y: Number((rot.y * THREE.MathUtils.RAD2DEG).toFixed(4)),
            z: Number((rot.z * THREE.MathUtils.RAD2DEG).toFixed(4))
        };
    },

    getWorldScale: function(element) {
        if (!element.object3D) return null;
        const scale = new THREE.Vector3();
        element.object3D.getWorldScale(scale);
        return {
            x: Number(scale.x.toFixed(4)),
            y: Number(scale.y.toFixed(4)),
            z: Number(scale.z.toFixed(4))
        };
    },

    getBoundingBox: function(element) {
        if (element.object3D) {
            const box = new THREE.Box3().setFromObject(element.object3D);
            const size = new THREE.Vector3();
            box.getSize(size);
            return {
                min: {
                    x: Number(box.min.x.toFixed(4)),
                    y: Number(box.min.y.toFixed(4)),
                    z: Number(box.min.z.toFixed(4))
                },
                max: {
                    x: Number(box.max.x.toFixed(4)),
                    y: Number(box.max.y.toFixed(4)),
                    z: Number(box.max.z.toFixed(4))
                },
                size: {
                    x: Number(size.x.toFixed(4)),
                    y: Number(size.y.toFixed(4)),
                    z: Number(size.z.toFixed(4))
                }
            };
        }
        return null;
    },

    collectSceneMetadata: function() {
        const scene = this.el;
        const renderer = scene.renderer;
        const camera = scene.camera;

        return {
            renderer: {
                type: renderer?.type || null,
                pixelRatio: window.devicePixelRatio,
                capabilities: {
                    webGL2: renderer?.capabilities?.isWebGL2 || false,
                    maxTextures: renderer?.capabilities?.maxTextures || null,
                    maxAttributes: renderer?.capabilities?.maxAttributes || null,
                    maxVertices: renderer?.capabilities?.maxVertices || null
                }
            },
            vr: {
                enabled: scene.is('vr-mode'),
                available: AFRAME.utils.device.checkHeadsetConnected()
            },
            camera: camera ? {
                fov: camera.fov,
                near: camera.near,
                far: camera.far,
                aspect: camera.aspect
            } : null,
            environment: {
                userAgent: navigator.userAgent,
                screenResolution: {
                    width: window.screen.width,
                    height: window.screen.height
                },
                timestamp: new Date().toISOString()
            }
        };
    },

    startAutoUpdate: function() {
        this.updateInterval = setInterval(this.collectSceneData, this.data.updateInterval);
    },

    stopAutoUpdate: function() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
    },

    findObjectById: function(id) {
        return this.sceneInfo.objects.find(obj => obj.id === id);
    },

    log: function(...args) {
        if (this.data.debug) {
            console.log('[Scene Collector]', ...args);
        }
    },

    remove: function() {
        this.stopAutoUpdate();
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        delete this.el.sceneCollector;
    }
});

// Usage example:
/*
<a-scene scene-collector="
    enableSocketIO: true;
    socketUrl: 'http://localhost:5000';
    socketRoom: 'room1';
    socketEventName: 'scene-data';
    enableAjax: true;
    endpoint: '/api/scene-data';
    autoUpdate: true;
    updateInterval: 1000;
    debug: true">
    <!-- Scene content -->
</a-scene>

// Listen for events
const scene = document.querySelector('a-scene');

// Socket.IO connection events
scene.addEventListener('scene-collector-socket-connected', () => {
    console.log('Socket.IO connected');
});

scene.addEventListener('scene-collector-socket-disconnected', () => {
    console.log('Socket.IO disconnected');
});

scene.addEventListener('scene-collector-socket-error', (event) => {
    console.error('Socket.IO error:', event.detail.error);
});

// Data collection and sending events
scene.addEventListener('scene-data-updated', (event) => {
    console.log('Scene data updated:', event.detail.data);
});

scene.addEventListener('scene-data-sent', (event) => {
    console.log('Data sent via:', event.detail.method);
    console.log('Success:', event.detail.success);
    if (!event.detail.success) {
        console.error('Error:', event.detail.error);
    }
});

// Manual data collection
const sceneCollector = scene.sceneCollector;
const sceneData = sceneCollector.collect();

// Get specific object data
const boxData = sceneCollector.getObjectById('my-box');

// Access Socket.IO instance for custom events
const socket = sceneCollector.socket();
*/


// ---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------


AFRAME.registerComponent('scene-updater', {
    schema: {
        socketUrl: { type: 'string', default: '' },  // Socket.IO server URL
        socketPath: { type: 'string', default: '/socket.io' },
        socketRoom: { type: 'string', default: '' },
        autoReconnect: { type: 'boolean', default: true },
        reconnectAttempts: { type: 'number', default: 5 },
        reconnectDelay: { type: 'number', default: 1000 },
        interpolate: { type: 'boolean', default: true }, // Smooth transitions
        interpolationDuration: { type: 'number', default: 200 }, // Duration in ms
        createMissingEntities: { type: 'boolean', default: true }, // Auto-create entities if missing
        debug: { type: 'boolean', default: false }  // Enable debug logging
    },

    init: function() {
        this.updateQueue = new Map(); // Queue for pending updates
        this.socket = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.isAnimating = false;
        
        // Bind methods
        this.processUpdate = this.processUpdate.bind(this);
        this.handleSocketMessage = this.handleSocketMessage.bind(this);
        this.initSocketConnection = this.initSocketConnection.bind(this);
        this.animate = this.animate.bind(this);

        // Load Socket.IO and initialize
        if (!window.io) {
            this.loadSocketIOScript()
                .then(() => this.initSocketConnection())
                .catch(error => {
                    this.log('Failed to load Socket.IO:', error);
                });
        } else {
            this.initSocketConnection();
        }

        // Expose public methods
        this.el.sceneUpdater = {
            sendUpdate: this.sendUpdate.bind(this),
            reconnect: this.reconnectSocket.bind(this),
            getConnectionStatus: () => this.isConnected,
            socket: () => this.socket
        };
    },

    loadSocketIOScript: function() {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/socket.io/4.7.2/socket.io.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
        });
    },

    initSocketConnection: function() {
        if (!this.data.socketUrl) {
            console.error('Socket.IO URL not provided');
            return;
        }

        this.socket = io(this.data.socketUrl, {
            path: this.data.socketPath,
            reconnection: this.data.autoReconnect,
            reconnectionAttempts: this.data.reconnectAttempts,
            reconnectionDelay: this.data.reconnectDelay,
            timeout: 10000
        });

        // Connection events
        this.socket.on('connect', () => {
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.log('Socket.IO connected');

            // Join room if specified
            if (this.data.socketRoom) {
                this.socket.emit('join-room', this.data.socketRoom);
            }

            this.el.emit('scene-updater-connected');
        });

        this.socket.on('disconnect', () => {
            this.isConnected = false;
            this.log('Socket.IO disconnected');
            this.el.emit('scene-updater-disconnected');
        });

        this.socket.on('connect_error', (error) => {
            this.log('Socket.IO connection error:', error);
            this.el.emit('scene-updater-error', { error });

            if (this.data.autoReconnect) {
                this.reconnectAttempts++;
                if (this.reconnectAttempts >= this.data.reconnectAttempts) {
                    this.log('Max reconnection attempts reached');
                }
            }
        });

        // Update events
        this.socket.on('scene-update', this.handleSocketMessage);
        this.socket.on('entity-update', (data) => this.processUpdate({ objects: [data] }));
        this.socket.on('entity-remove', (entityId) => this.removeEntity(entityId));
    },

    handleSocketMessage: function(data) {
        try {
            this.processUpdate(data);
        } catch (error) {
            this.log('Error processing Socket.IO message:', error);
            this.el.emit('scene-updater-error', { error });
        }
    },

    processUpdate: function(data) {
        if (!Array.isArray(data.objects)) {
            this.log('Invalid update data format');
            return;
        }

        data.objects.forEach(objectData => {
            const element = document.querySelector(`#${objectData.id}`);
            if (!element) {
                if (this.data.createMissingEntities) {
                    this.createEntity(objectData);
                } else {
                    this.log(`Entity not found: ${objectData.id}`);
                }
            } else {
                this.updateEntity(element, objectData);
            }
        });

        // Handle removals if specified
        if (data.removals && Array.isArray(data.removals)) {
            data.removals.forEach(id => this.removeEntity(id));
        }

        this.el.emit('scene-update-processed', { data });
    },

    createEntity: function(objectData) {
        const entity = document.createElement('a-entity');
        entity.id = objectData.id;
        
        // Apply initial attributes
        this.applyAttributes(entity, objectData);
        
        // Add to scene
        this.el.appendChild(entity);
        
        this.log('Created new entity:', objectData.id);
    },

    removeEntity: function(entityId) {
        const element = document.querySelector(`#${entityId}`);
        if (element) {
            element.parentNode.removeChild(element);
            this.log('Removed entity:', entityId);
        }
    },

    updateEntity: function(element, objectData) {
        if (this.data.interpolate) {
            // Queue the update for interpolation
            this.updateQueue.set(element.id, {
                element: element,
                startState: this.getCurrentState(element),
                targetState: objectData,
                startTime: Date.now()
            });
            
            // Start animation if not already running
            if (!this.isAnimating) {
                this.isAnimating = true;
                this.animate();
            }
        } else {
            // Immediate update without interpolation
            this.applyAttributes(element, objectData);
        }
    },

    getCurrentState: function(element) {
        return {
            position: element.getAttribute('position') || { x: 0, y: 0, z: 0 },
            rotation: element.getAttribute('rotation') || { x: 0, y: 0, z: 0 },
            scale: element.getAttribute('scale') || { x: 1, y: 1, z: 1 }
        };
    },

    animate: function() {
        const currentTime = Date.now();

        for (const [id, update] of this.updateQueue.entries()) {
            const progress = Math.min(
                (currentTime - update.startTime) / this.data.interpolationDuration,
                1
            );

            if (progress < 1) {
                // Interpolate values
                const interpolated = this.interpolateStates(
                    update.startState,
                    update.targetState,
                    progress
                );
                this.applyAttributes(update.element, interpolated);
            } else {
                // Final update
                this.applyAttributes(update.element, update.targetState);
                this.updateQueue.delete(id);
            }
        }

        if (this.updateQueue.size > 0) {
            requestAnimationFrame(this.animate);
        } else {
            this.isAnimating = false;
        }
    },

    interpolateStates: function(start, target, progress) {
        // Use a smooth easing function
        const eased = this.easeInOutCubic(progress);
        
        const interpolate = (a, b) => {
            if (typeof b === 'number') return a + (b - a) * eased;
            return a;
        };
        
        const result = { ...target };

        // Interpolate transforms if they exist
        if (target.position && start.position) {
            result.position = {
                x: interpolate(start.position.x, target.position.x),
                y: interpolate(start.position.y, target.position.y),
                z: interpolate(start.position.z, target.position.z)
            };
        }

        if (target.rotation && start.rotation) {
            result.rotation = {
                x: interpolate(start.rotation.x, target.rotation.x),
                y: interpolate(start.rotation.y, target.rotation.y),
                z: interpolate(start.rotation.z, target.rotation.z)
            };
        }

        if (target.scale && start.scale) {
            result.scale = {
                x: interpolate(start.scale.x, target.scale.x),
                y: interpolate(start.scale.y, target.scale.y),
                z: interpolate(start.scale.z, target.scale.z)
            };
        }

        return result;
    },

    easeInOutCubic: function(t) {
        return t < 0.5 
            ? 4 * t * t * t 
            : 1 - Math.pow(-2 * t + 2, 3) / 2;
    },

    applyAttributes: function(element, data) {
        // Handle position, rotation, scale
        if (data.position) element.setAttribute('position', data.position);
        if (data.rotation) element.setAttribute('rotation', data.rotation);
        if (data.scale) element.setAttribute('scale', data.scale);

        // Handle material updates
        if (data.material) {
            element.setAttribute('material', data.material);
        }

        // Handle geometry updates
        if (data.geometry) {
            element.setAttribute('geometry', data.geometry);
        }

        // Handle visibility
        if (data.visible !== undefined) {
            element.setAttribute('visible', data.visible);
        }

        // Handle components
        if (data.components) {
            Object.entries(data.components).forEach(([name, value]) => {
                element.setAttribute(name, value);
            });
        }

        // Handle any other attributes
        if (data.attributes) {
            Object.entries(data.attributes).forEach(([name, value]) => {
                element.setAttribute(name, value);
            });
        }
    },

    sendUpdate: function(data) {
        if (!this.socket || !this.isConnected) {
            this.log('Socket not connected');
            return;
        }

        try {
            if (data.objects && data.objects.length === 1) {
                // Send individual entity update
                this.socket.emit('entity-update', {
                    roomId: this.data.socketRoom,
                    data: data.objects[0]
                });
            } else {
                // Send batch update
                this.socket.emit('scene-update', {
                    roomId: this.data.socketRoom,
                    data: data
                });
            }
        } catch (error) {
            this.log('Error sending update:', error);
            this.el.emit('scene-updater-error', { error });
        }
    },

    reconnectSocket: function() {
        if (this.socket) {
            this.socket.connect();
        } else {
            this.initSocketConnection();
        }
    },

    log: function(...args) {
        if (this.data.debug) {
            console.log('[Scene Updater]', ...args);
        }
    },

    remove: function() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket.removeAllListeners();
        }
        this.updateQueue.clear();
        this.isAnimating = false;
        delete this.el.sceneUpdater;
    }
});

// Usage example:
/*
<a-scene scene-updater="
    socketUrl: 'http://localhost:5000';
    socketRoom: 'room1';
    interpolate: true;
    interpolationDuration: 200;
    createMissingEntities: true;
    debug: true">
    <!-- Scene content -->
</a-scene>

// Listen for events
const scene = document.querySelector('a-scene');

// Connection events
scene.addEventListener('scene-updater-connected', () => {
    console.log('Connected to Socket.IO server');
});

scene.addEventListener('scene-updater-disconnected', () => {
    console.log('Disconnected from Socket.IO server');
});

scene.addEventListener('scene-updater-error', (event) => {
    console.error('Error:', event.detail.error);
});

scene.addEventListener('scene-update-processed', (event) => {
    console.log('Update processed:', event.detail.data);
});

// Send updates
const updater = scene.sceneUpdater;

// Send individual entity update
updater.sendUpdate({
    objects: [{
        id: 'box1',
        position: { x: 1, y: 2, z: 3 },
        rotation: { x: 0, y: 45, z: 0 },
        material: { color: 'red' }
    }]
});

// Send batch update
updater.sendUpdate({
    objects: [
        {
            id: 'box1',
            position: { x: 1, y: 2, z: 3 }
        },
        {
            id: 'sphere1',
            scale: { x: 2, y: 2, z: 2 }
        }
    ]
});

// Remove entities
updater.sendUpdate({
    removals: ['box1', 'sphere1']
});

// Get Socket.IO instance for custom events
const socket = updater.socket();
*/


export const UtilComp = {
    sceneCollector: AFRAME.components['scene-collector'],
    sceneUpdater: AFRAME.components['scene-updater']
  };